/**
 * /ndt-inspection/jobs — the NDT job register (GET /ndt-inspection/jobs,
 * fieldiq-api#1144). NDT jobs are their own table (NDJ-0001…), not casing jobs.
 *
 * Full width; a row opens the job on its own page. The filters (status chip,
 * customer, search) live in the URL so the back arrow returns to the list as
 * it was left (pages/downhole/recordPage.ts).
 *
 * CHIP COUNTS are a tally of the UNFILTERED list. Honest here because the
 * endpoint is unpaginated and no endpoint returns per-status counts; the list
 * itself is filtered by the API (status, customerId, q).
 *
 * Weld counts and the repair rate per row are the API's; nothing is computed.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Activity, CalendarClock, ClipboardList, Plus, Search } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useNdtCustomers, useNdtJobs } from '../../hooks/useNdtInspection';
import { queryLoadFailure } from '../../lib/load-failure';
import { canDispatchNdt, formatRepairRate, ndtJobStatusSkin, ndtLocationRefKind } from '../../lib/ndt-inspection';
import { NDT_JOB_STATUSES, type NdtJob } from '../../types/ndt-inspection';
import { useOpenRecord } from '../downhole/recordPage';
import { NDT_INPUT, NdtBadge, NdtChip, NdtChipRow, NdtEmpty, NdtPanel, NdtRegister, ndtDay, type NdtColumn } from './NdtBits';
import { NdtJobDialog } from './NdtJobDialogs';

const STATUS_CHIPS: { key: string; label: string }[] = [
  { key: '', label: 'All' },
  ...NDT_JOB_STATUSES.map((s) => ({ key: s, label: ndtJobStatusSkin(s).label })),
];

/**
 * Where: location, then the legal location, then line. None → `—`. The legal
 * location is prefixed "LSD" only on a Canadian job; a US job's county reads bare.
 */
export function ndtJobWhere(j: Pick<NdtJob, 'locationName' | 'lsd' | 'lineRef'> & Partial<Pick<NdtJob, 'provinceState' | 'currency'>>): string {
  const ref = j.lsd ? (ndtLocationRefKind(j.provinceState, j.currency) === 'ca' ? `LSD ${j.lsd}` : j.lsd) : null;
  const parts = [j.locationName, ref].filter(Boolean);
  return parts.length ? parts.join(' · ') : j.lineRef ? `Line ${j.lineRef}` : '—';
}

/** "3 of 8 accepted" from the API's weld counts; no welds → "No welds". */
export function ndtWeldProgress(c: NdtJob['weldCounts'] | null | undefined): string {
  if (!c || typeof c.total !== 'number') return '—';
  if (c.total === 0) return 'No welds';
  return `${c.accept} of ${c.total} accepted`;
}

export default function JobsScreen() {
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const customerId = params.get('customer') ?? '';
  const q = params.get('q') ?? '';
  const [search, setSearch] = useState(q);
  const [adding, setAdding] = useState(false);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };
  useEffect(() => {
    const t = setTimeout(() => {
      if (search.trim() !== q) setParam('q', search.trim());
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const listQuery = useNdtJobs({ status: status || undefined, customerId: customerId || undefined, q: q || undefined });
  const allQuery = useNdtJobs();
  const customers = useNdtCustomers();
  const jobs = listQuery.data?.data ?? [];
  const all = allQuery.isLoading || queryLoadFailure(allQuery) ? undefined : allQuery.data?.data;
  const countOf = (pick: (j: NdtJob) => boolean) => (all ? all.filter(pick).length : undefined);
  const canAdd = canDispatchNdt(user?.role);

  const columns: NdtColumn<NdtJob>[] = [
    { key: 'number', header: 'Job', render: (j) => <span className="font-mono font-extrabold text-white">{j.jobNumber || '—'}</span> },
    { key: 'customer', header: 'Customer', render: (j) => j.customerName || '—' },
    { key: 'where', header: 'Location', render: (j) => ndtJobWhere(j) },
    { key: 'code', header: 'Code', render: (j) => j.acceptanceCodeLabel || '—' },
    { key: 'scheduled', header: 'Scheduled', render: (j) => ndtDay(j.scheduledDate) },
    { key: 'welds', header: 'Welds', render: (j) => ndtWeldProgress(j.weldCounts) },
    { key: 'repair', header: 'Repair rate', align: 'right', render: (j) => formatRepairRate(j.repairRate?.rate) },
    { key: 'status', header: 'Status', render: (j) => <NdtBadge skin={ndtJobStatusSkin(j.status)} value={j.status} /> },
  ];

  const filtered = Boolean(status || customerId || q);

  return (
    <div data-testid="ndt-jobs-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="NDT inspection"
        title="NDT Jobs"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          canAdd ? (
            <button type="button" onClick={() => setAdding(true)} className="fi-action fi-action--standard fi-action--primary">
              <Plus className="size-4" aria-hidden="true" />
              New job
            </button>
          ) : undefined
        }
        stats={[
          { icon: <ClipboardList className="size-4" aria-hidden="true" />, label: 'Jobs', sub: 'On record', tone: 'neutral', loading: allQuery.isLoading, value: displayValue(countOf(() => true)) },
          { icon: <Activity className="size-4" aria-hidden="true" />, label: 'Active', sub: 'In progress', tone: 'accent', loading: allQuery.isLoading, value: displayValue(countOf((j) => j.status === 'active')) },
          { icon: <CalendarClock className="size-4" aria-hidden="true" />, label: 'Scheduled', sub: 'Not started', tone: 'neutral', loading: allQuery.isLoading, value: displayValue(countOf((j) => j.status === 'scheduled')) },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <NdtChipRow label="Filter by status">
            {STATUS_CHIPS.map((c) => (
              <NdtChip
                key={c.key || 'all'}
                label={c.label}
                active={status === c.key}
                count={all ? countOf((j) => !c.key || j.status === c.key) : undefined}
                onClick={() => setParam('status', c.key)}
              />
            ))}
          </NdtChipRow>
          <div className="grid gap-3 md:grid-cols-[1fr_280px]">
            <label className="relative block">
              <span className="sr-only">Search job, customer, line, location or PO</span>
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fieldiq-muted" aria-hidden="true" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search job, customer, line, location or PO"
                className="h-10 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card pl-9 pr-3 text-sm text-white placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
              />
            </label>
            <label>
              <span className="sr-only">Customer</span>
              <select aria-label="Customer" className={NDT_INPUT} value={customerId} onChange={(e) => setParam('customer', e.target.value)}>
                <option value="">All customers</option>
                {(customers.data ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <NdtPanel
            eyebrow="Job register"
            title="Jobs"
            testId="ndt-job-register"
            footer={
              !listQuery.isLoading && !queryLoadFailure(listQuery) && jobs.length > 0
                ? filtered && all
                  ? `Showing ${jobs.length} of ${all.length} jobs`
                  : `${jobs.length} job${jobs.length === 1 ? '' : 's'}`
                : undefined
            }
          >
            <NdtRegister
              query={listQuery}
              rows={jobs}
              columns={columns}
              rail={(j) => ndtJobStatusSkin(j.status).rail}
              onOpen={(j) => openRecord(`/ndt-inspection/jobs/${j.id}`)}
              subject="NDT jobs"
              testId="ndt-job"
              minWidth="min-w-[1080px]"
              empty={filtered ? <NdtEmpty>No job matches these filters.</NdtEmpty> : <NdtEmpty>No NDT jobs are on record.</NdtEmpty>}
              card={(j) => (
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-mono text-sm font-extrabold text-white">{j.jobNumber || '—'}</p>
                    <p className="mt-0.5 truncate text-sm text-fieldiq-text">{j.customerName || '—'}</p>
                    <p className="mt-0.5 text-xs text-fieldiq-muted">
                      {ndtJobWhere(j)} · {ndtDay(j.scheduledDate)} · {ndtWeldProgress(j.weldCounts)}
                    </p>
                  </div>
                  <NdtBadge skin={ndtJobStatusSkin(j.status)} value={j.status} />
                </div>
              )}
            />
          </NdtPanel>
        </div>
      </section>

      {canAdd && (
        <NdtJobDialog
          open={adding}
          onClose={() => setAdding(false)}
          meta={listQuery.data?.meta}
          onCreated={(id) => openRecord(`/ndt-inspection/jobs/${id}`)}
        />
      )}
    </div>
  );
}
